<template>
  <div class="log-manage">
    <el-card shadow="never" class="filter-panel">
      <el-form :model="searchForm" class="search-form" label-width="80px">
        <div class="filter-grid">
          <el-form-item label="操作者">
            <el-input
              v-model="searchForm.actorKeyword"
              placeholder="用户名/手机号"
              clearable
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="操作类型">
            <el-select v-model="searchForm.actionType" placeholder="全部" clearable>
              <el-option
                v-for="item in actionTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="操作端">
            <el-select v-model="searchForm.clientType" placeholder="全部" clearable>
              <el-option label="管理后台" value="MANAGE_WEB" />
              <el-option label="微信小程序" value="MINI_PROGRAM" />
            </el-select>
          </el-form-item>
          <el-form-item label="操作对象">
            <el-select v-model="searchForm.resourceType" placeholder="全部" clearable>
              <el-option
                v-for="item in resourceTypeOptions"
                :key="item.value || 'all'"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="操作结果">
            <el-select v-model="searchForm.outcome" placeholder="全部" clearable>
              <el-option label="成功" value="SUCCESS" />
              <el-option label="失败" value="FAILURE" />
            </el-select>
          </el-form-item>
          <el-form-item label="操作时间">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
            />
          </el-form-item>
          <el-form-item label-width="20px">
            <el-button type="primary" icon="el-icon-search" :loading="searchLoading" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
          </el-form-item>
        </div>
      </el-form>
    </el-card>

    <div class="audit-summary">
      <div class="summary-item">
        <span class="summary-label">当前操作结果</span>
        <strong>{{ total }}</strong>
      </div>
      <div class="summary-item danger">
        <span class="summary-label">删除/重点事件</span>
        <strong>{{ highlightedCount }}</strong>
      </div>
      <div class="summary-hint">每条日志记录操作者、操作时间、IP、操作端、业务对象和可追溯请求信息。</div>
    </div>

    <el-card shadow="never" class="table-card">
      <el-table :data="tableData" v-loading="loading" border stripe row-key="id">
        <el-table-column type="index" label="序号" width="64" align="center" :index="tableIndex" />
        <el-table-column label="操作者" width="170" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ actorName(row) }}</template>
        </el-table-column>
        <el-table-column label="操作类型" width="104" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="actionTagType(row)" size="small">{{ actionLabel(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作端" width="112" align="center">
          <template slot-scope="{ row }">
            <el-tag type="info" size="small">{{ clientLabel(row) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="ip" label="IP" width="138" show-overflow-tooltip />
        <el-table-column prop="createTime" label="操作时间" width="158" />
        <el-table-column label="行为内容" min-width="360">
          <template slot-scope="{ row }">
            <div class="behavior-main">
              <div class="behavior-sentence">{{ behaviorSentence(row) }}</div>
              <div class="behavior-meta">
                <el-tag size="mini" :type="actionTagType(row)">{{ actionLabel(row) }}</el-tag>
                <el-tag size="mini" type="info">{{ clientLabel(row) }}</el-tag>
                <span>{{ resourceLabel(row) }}：{{ targetName(row) }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作结果" width="92" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.outcome === 'FAILURE' ? 'danger' : 'success'" size="small">
              {{ row.outcome === 'FAILURE' ? '失败' : '成功' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="88" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <div class="pagination-wrapper">
      <el-pagination
        class="pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog title="行为日志溯源" :visible.sync="detailVisible" width="760px" append-to-body>
      <div v-if="detailRow" class="detail-panel">
        <div class="detail-sentence">{{ behaviorSentence(detailRow) }}</div>
        <div class="detail-grid">
          <div><label>操作者</label><span>{{ actorName(detailRow) }}</span></div>
          <div><label>操作时间</label><span>{{ detailRow.createTime || '—' }}</span></div>
          <div><label>来源IP</label><span>{{ detailRow.ip || '—' }}</span></div>
          <div><label>操作端</label><span>{{ clientLabel(detailRow) }}</span></div>
          <div><label>业务对象</label><span>{{ resourceLabel(detailRow) }}</span></div>
          <div><label>对象名称</label><span>{{ targetName(detailRow) }}</span></div>
          <div><label>资源ID</label><span>{{ detailRow.resourceId || '—' }}</span></div>
          <div><label>请求</label><span>{{ detailRow.method || '—' }} {{ detailRow.path || '—' }}</span></div>
          <div><label>账号ID</label><span>{{ detailRow.userId || '—' }}</span></div>
          <div><label>角色</label><span>{{ actorRoleName(detailRow) }}</span></div>
        </div>
        <div v-if="getChanges(detailRow).length" class="detail-section">
          <div class="section-title">变更明细</div>
          <div class="change-list detail-changes">
            <div v-for="item in getChanges(detailRow)" :key="item.field + item.label" class="change-item">
              <span class="change-field">{{ item.label }}</span>
              <span class="change-old">{{ item.oldValue }}</span>
              <i class="el-icon-right change-arrow"></i>
              <span class="change-new">{{ item.newValue }}</span>
            </div>
          </div>
        </div>
        <div class="detail-section">
          <div class="section-title">原始上下文</div>
          <pre class="json-box">{{ formatJson(detailRow.extra) }}</pre>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '../services/api';

const ACTION_LABEL = {
  CREATE: '新增',
  UPDATE: '编辑',
  DELETE: '删除',
  LOGIN: '登录',
  LOGOUT: '登出'
};

const ACTION_TYPE_OPTIONS = Object.keys(ACTION_LABEL).map(value => ({
  value,
  label: ACTION_LABEL[value]
}));

const CLIENT_LABEL = {
  MANAGE_WEB: '管理后台',
  MINI_PROGRAM: '微信小程序',
  UNKNOWN: '未知来源'
};

const RESOURCE_LABEL = {
  student: '学员',
  org: '机构',
  staff: '会员',
  user: '平台账号',
  role: '角色',
  dict: '字典项'
};

const RESOURCE_TYPE_OPTIONS = [
  { label: '全部', value: '' },
  { label: '学员', value: 'student' },
  { label: '机构', value: 'org' },
  { label: '会员', value: 'staff' }
];

export default {
  name: 'LogManage',
  data() {
    return {
      loading: false,
      searchLoading: false,
      tableData: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      dateRange: this.defaultDateRange(),
      actionTypeOptions: ACTION_TYPE_OPTIONS,
      resourceTypeOptions: RESOURCE_TYPE_OPTIONS,
      detailVisible: false,
      detailRow: null,
      searchForm: {
        actorKeyword: '',
        actionType: '',
        clientType: '',
        outcome: '',
        resourceType: ''
      }
    };
  },
  computed: {
    highlightedCount() {
      return this.tableData.filter(row => row.highlight === 1 || row.highlight === true || this.actionType(row) === 'DELETE').length;
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    parseExtra(extra) {
      if (!extra) return {};
      if (typeof extra === 'object') return extra;
      try {
        return JSON.parse(extra);
      } catch (e) {
        return {};
      }
    },
    behavior(row) {
      const extra = this.parseExtra(row.extra);
      return extra.auditBehavior || {};
    },
    actionType(row) {
      const behavior = this.behavior(row);
      if (behavior.actionType) return behavior.actionType;
      const action = String(row.eventAction || '').toUpperCase();
      if (/LOGIN/.test(action)) return 'LOGIN';
      if (/LOGOUT/.test(action)) return 'LOGOUT';
      if (/(^|_)(DELETE|REMOVE)$/.test(action)) return 'DELETE';
      if (/(^|_)(UPDATE|SAVE)$/.test(action)) return 'UPDATE';
      if (/(^|_)(CREATE|ADD)$/.test(action)) return 'CREATE';
      return '';
    },
    actionLabel(row) {
      const behavior = this.behavior(row);
      return behavior.actionLabel || ACTION_LABEL[this.actionType(row)] || '—';
    },
    actionTagType(row) {
      const type = this.actionType(row);
      if (type === 'DELETE') return 'danger';
      if (type === 'UPDATE') return 'warning';
      if (type === 'CREATE') return 'success';
      if (type === 'LOGIN') return 'success';
      if (type === 'LOGOUT') return 'info';
      return 'info';
    },
    clientLabel(row) {
      const behavior = this.behavior(row);
      if (behavior.clientLabel) return behavior.clientLabel;
      if (behavior.clientType) return CLIENT_LABEL[behavior.clientType] || behavior.clientType;
      return row.actorRoleCode === 'staff' ? '微信小程序' : '管理后台';
    },
    actorName(row) {
      return row.actorAccount || row.userId || '未知用户';
    },
    actorRoleName(row) {
      if (!row) return '其他';
      return row.actorRoleName || '其他';
    },
    resourceLabel(row) {
      const behavior = this.behavior(row);
      return behavior.resourceLabel || RESOURCE_LABEL[row.resourceType] || row.resourceType || '业务对象';
    },
    targetName(row) {
      const behavior = this.behavior(row);
      return behavior.targetName || row.resourceId || '未命名对象';
    },
    getChanges(row) {
      const behavior = this.behavior(row);
      return Array.isArray(behavior.changes) ? behavior.changes : [];
    },
    behaviorSentence(row) {
      const summary = row.summary || row.operation || '执行了操作';
      return `${this.actorName(row)} 在 ${row.createTime || '未知时间'} 于 ${row.ip || '未知IP'} 通过${this.clientLabel(row)}${summary}`;
    },
    defaultDateRange() {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return [this.formatDate(start), this.formatDate(end)];
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    tableIndex(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },
    formatJson(extra) {
      if (!extra) return '—';
      const obj = this.parseExtra(extra);
      if (!Object.keys(obj).length) return typeof extra === 'string' ? extra : JSON.stringify(extra, null, 2);
      return JSON.stringify(obj, null, 2);
    },
    async loadData() {
      this.loading = true;
      try {
        const params = {
          actorKeyword: this.searchForm.actorKeyword,
          actionType: this.searchForm.actionType,
          clientType: this.searchForm.clientType,
          outcome: this.searchForm.outcome,
          resourceType: this.searchForm.resourceType,
          startDate: this.dateRange && this.dateRange[0] ? this.dateRange[0] : '',
          endDate: this.dateRange && this.dateRange[1] ? this.dateRange[1] : '',
          page: this.currentPage,
          pageSize: this.pageSize
        };
        const res = await api.post('/log/list', params);
        if (res.data.success) {
          this.tableData = res.data.data.list || [];
          this.total = res.data.data.total || 0;
        }
      } catch (e) {
        this.$message.error('加载日志失败');
      }
      this.loading = false;
    },
    async handleSearch() {
      this.searchLoading = true;
      this.currentPage = 1;
      await this.loadData();
      this.searchLoading = false;
    },
    handleReset() {
      this.searchForm = {
        actorKeyword: '',
        actionType: '',
        clientType: '',
        outcome: '',
        resourceType: ''
      };
      this.dateRange = this.defaultDateRange();
      this.handleSearch();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.loadData();
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.loadData();
    },
    openDetail(row) {
      this.detailRow = row;
      this.detailVisible = true;
    }
  }
};
</script>

<style scoped>
.log-manage {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 56px;
}

.filter-panel {
  margin-bottom: 12px;
}

.search-form {
  width: 100%;
}

.filter-grid {
  display: flex;
  flex-wrap:wrap;
  row-gap: 10px;
}

.filter-grid >>> .el-form-item {
  margin: 0;
}

.filter-grid >>> .el-input,
.filter-grid >>> .el-select,
{
  width: 200px;
}

.audit-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 42px;
  padding: 0 4px 12px;
  color: #606266;
}

.summary-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.summary-item strong {
  font-size: 20px;
  color: #303133;
}

.summary-item.danger strong {
  color: #f56c6c;
}

.summary-label {
  font-size: 13px;
}

.summary-hint {
  font-size: 13px;
  margin-left: auto;
}

.table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.table-card >>> .el-table {
  flex: 1;
}

.behavior-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
}

.behavior-sentence {
  color: #303133;
  line-height: 1.45;
  font-weight: 500;
}

.behavior-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #909399;
  font-size: 12px;
}

.expand-panel {
  padding: 10px 18px 14px;
  background: #fafafa;
}

.expand-title,
.section-title {
  margin-bottom: 10px;
  font-weight: 600;
  color: #303133;
}

.change-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.change-item {
  display: grid;
  grid-template-columns: 120px minmax(120px, 1fr) 28px minmax(120px, 1fr);
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.change-field {
  color: #606266;
}

.change-old,
.change-new {
  min-width: 0;
  padding: 6px 8px;
  border-radius: 4px;
  word-break: break-word;
}

.change-old {
  color: #909399;
  background: #f4f4f5;
}

.change-new {
  color: #1f7a3f;
  background: #edf8f0;
}

.change-arrow {
  color: #909399;
  text-align: center;
}

.trace-grid,
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 18px;
  color: #606266;
  font-size: 13px;
}

.detail-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-sentence {
  padding: 12px 14px;
  border-left: 3px solid #409eff;
  background: #f5f9ff;
  color: #303133;
  line-height: 1.6;
}

.detail-grid div {
  display: flex;
  gap: 8px;
  min-width: 0;
}

.detail-grid label {
  flex: 0 0 72px;
  color: #909399;
}

.detail-grid span {
  min-width: 0;
  color: #303133;
  word-break: break-word;
}

.detail-section {
  border-top: 1px solid #ebeef5;
  padding-top: 14px;
}

.detail-changes {
  max-height: 220px;
  overflow: auto;
}

.json-box {
  max-height: 260px;
  margin: 0;
  padding: 12px;
  overflow: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
  color: #303133;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 960px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .audit-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .summary-hint {
    margin-left: 0;
  }

  .trace-grid,
  .detail-grid,
  .change-item {
    grid-template-columns: 1fr;
  }

  .change-arrow {
    display: none;
  }
}

@media (max-width: 640px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }
}
</style>
